import { Logo } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - ezRecipeBox</title>
        <meta name="description" content="Privacy Policy for ezRecipeBox - Learn how we collect, use, and protect your personal information." />
      </Helmet>
      <div className="flex flex-col min-h-screen">
        <header className="px-4 lg:px-6 h-16 flex items-center shadow-sm">
          <Link to="/" className="flex items-center justify-center">
            <Logo className="h-6 w-6 text-primary" />
            <span className="sr-only">ezRecipeBox</span>
          </Link>
          <nav className="ml-auto flex gap-4 sm:gap-6">
            <Button variant="ghost" asChild>
              <Link to="/login">
                Login
              </Link>
            </Button>
            <Button asChild>
              <Link to="/signup">
                Sign Up
              </Link>
            </Button>
          </nav>
        </header>
        <main className="flex-1 container mx-auto px-4 py-8 md:py-12 max-w-4xl">
          <article className="prose prose-slate max-w-none dark:prose-invert">
            <h1 className="font-headline text-4xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground mb-6">
              <strong>Last Updated:</strong> January 2025
            </p>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">1. Introduction</h2>
              <p>
                Welcome to ezRecipeBox (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). We are committed to protecting your privacy and ensuring you have a positive experience on our website and in using our products and services. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our recipe management application.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">2. Information We Collect</h2>
              <h3 className="font-semibold text-xl mt-6 mb-3">2.1 Information You Provide to Us</h3>
              <ul>
                <li><strong>Account Information:</strong> When you create an account, we collect your email address, display name, and password (stored securely using industry-standard encryption).</li>
                <li><strong>Profile Information:</strong> We may collect profile photos and other information you choose to provide in your user profile.</li>
                <li><strong>Recipe Content:</strong> Recipes, images, videos, and other content you create or upload to the service.</li>
                <li><strong>Recipe Box Information:</strong> Recipe box names, descriptions, and member lists that you create or participate in.</li>
              </ul>

              <h3 className="font-semibold text-xl mt-6 mb-3">2.2 Information Automatically Collected</h3>
              <ul>
                <li><strong>Usage Data:</strong> Information about how you use our service, including pages visited, features used, and time spent on the service.</li>
                <li><strong>Device Information:</strong> Device type, operating system, browser type, and IP address.</li>
                <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar technologies to enhance your experience and analyze service usage.</li>
              </ul>

              <h3 className="font-semibold text-xl mt-6 mb-3">2.3 Information from Third-Party Services</h3>
              <p>
                If you choose to sign in using Google OAuth, we may receive information from Google including your name, email address, and profile picture, as authorized by you during the sign-in process.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide, maintain, and improve our services</li>
                <li>Create and manage your account</li>
                <li>Process transactions and send related information</li>
                <li>Send you technical notices, updates, and support messages</li>
                <li>Respond to your comments and questions</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Personalize your experience</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">4. How We Share Your Information</h2>
              <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
              <ul>
                <li><strong>With Other Users:</strong> When you create or join a recipe box, other members of that recipe box can see your display name, profile photo, and the recipes you add.</li>
                <li><strong>Service Providers:</strong> We may share information with third-party service providers who perform services on our behalf, such as hosting (Firebase/Google Cloud), analytics, and payment processing.</li>
                <li><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to valid legal requests.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">5. Data Storage and Security</h2>
              <p>
                Your data is stored securely using Firebase services provided by Google Cloud Platform. We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">6. Your Rights and Choices</h2>
              <p>You have the right to:</p>
              <ul>
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Update:</strong> Update or correct your account information at any time</li>
                <li><strong>Delete:</strong> Delete your account and associated data</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications (if applicable)</li>
                <li><strong>Data Portability:</strong> Request a copy of your data</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us using the information provided in the Contact section below.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">7. Cookies and Tracking Technologies</h2>
              <p>
                We use cookies and similar tracking technologies to track activity on our service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">8. Children&apos;s Privacy</h2>
              <p>
                Our service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can delete such information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">9. International Data Transfers</h2>
              <p>
                Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By using our service, you consent to the transfer of your information to facilities located in the United States or other countries where our service providers operate.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">10. Changes to This Privacy Policy</h2>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the &quot;Last Updated&quot; date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-headline text-2xl font-semibold mt-8 mb-4">11. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <p className="mt-4">
                <strong>Email:</strong> [Your Support Email Address]<br />
                <strong>Website:</strong> https://[your-domain].com
              </p>
            </section>
          </article>
        </main>
        <footer className="flex flex-col gap-2 sm:flex-row py-6 w-full shrink-0 items-center px-4 md:px-6 border-t justify-between">
          <p className="text-xs text-muted-foreground">&copy; 2025 ezRecipeBox. All rights reserved.</p>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground underline">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-foreground underline">
              Terms of Service
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
}

